import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { ExportDialog } from '../components/ExportDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Users, TrendingUp, CheckCircle2, Clock, XCircle, Loader2,
  BarChart3, Zap, Sun, Tv, BatteryCharging, Shield, Filter,
  ArrowUpDown, Handshake, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_CONFIG = {
  energias: { label: 'Energias', icon: Zap, color: 'text-yellow-500' },
  luz_gas: { label: 'Luz e Gas', icon: Zap, color: 'text-yellow-500' },
  solar: { label: 'Solar', icon: Sun, color: 'text-amber-500' },
  telecomunicacoes: { label: 'Telecom', icon: Tv, color: 'text-purple-500' },
  carregamento: { label: 'Carreg.', icon: BatteryCharging, color: 'text-green-500' },
  alarmes: { label: 'Alarmes', icon: Shield, color: 'text-red-500' },
};

const STATUS_COLORS = {
  concluido: 'bg-emerald-100 text-emerald-700',
  em_processo: 'bg-blue-100 text-blue-700',
  pendente: 'bg-amber-100 text-amber-700',
  anulado: 'bg-red-100 text-red-700',
  pedido_chamada: 'bg-slate-100 text-slate-700',
  agendado: 'bg-cyan-100 text-cyan-700',
};

const STATUS_LABELS = {
  concluido: 'Concluido',
  em_processo: 'Em Processo',
  pendente: 'Pendente',
  anulado: 'Anulado',
  pedido_chamada: 'Ped. Chamada',
  agendado: 'Agendado',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  gestor: 'Gestor',
  admin: 'Admin',
  parceiro: 'Parceiro',
  backoffice: 'Backoffice',
  comercial: 'Comercial',
  chefe_equipa: 'Chefe de Equipa',
  angariador: 'Angariador',
};

export const ComercialsDashboard = () => {
  const { user, isFullAccess } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerParam = searchParams.get('partner') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [partnerFilter, setPartnerFilter] = useState(partnerParam);
  const [partners, setPartners] = useState([]);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState([]);

  // Export
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/partners`);
      setPartners(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchData = async (pFilter) => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const filterVal = pFilter !== undefined ? pFilter : partnerFilter;
      if (filterVal) params.partner_id = filterVal;
      const res = await axios.get(`${API_URL}/dashboard/comerciais`, { params });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPartners();
    fetchData();
  }, []);

  const handleFilter = () => fetchData();
  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setPartnerFilter('');
    setSelectedIds([]);
    setTimeout(() => fetchData(''), 0);
  };

  const handlePartnerChange = (val) => {
    const v = val === 'all' ? '' : val;
    setPartnerFilter(v);
    setSelectedIds([]);
    fetchData(v);
  };

  const comerciais = data?.comerciais || [];
  const totals = data?.totals || {};

  const toggleId = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelectedIds(prev =>
      prev.length === comerciais.length ? [] : comerciais.map(c => c.user_id)
    );
  };

  const handleExport = async (columns) => {
    const ids = selectedIds.length > 0 ? selectedIds : comerciais.map(c => c.user_id);
    if (ids.length === 0) { toast.error('Sem comerciais para exportar'); return; }
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/production/export-excel`, {
        type: 'comerciais',
        entity_ids: ids,
        columns,
        start_date: startDate || null,
        end_date: endDate || null,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disp = res.headers['content-disposition'];
      const fname = disp ? disp.split('filename="')[1]?.replace('"', '') : 'producao_comerciais.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
      setShowExport(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="comerciais-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Users className="w-6 h-6 text-[#2D3E6F]" />
              Producao de Comerciais
            </h1>
            <p className="text-slate-500 mt-1">
              Producao por comercial
              {partnerFilter ? ` - ${partners.find(p => p.id === partnerFilter)?.name || (partnerFilter === '__internal__' ? 'Equipa Interna' : '')}` : ''}
              {startDate || endDate ? ' (filtrado)' : ''}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => window.print()} variant="outline" className="gap-2 print:hidden" data-testid="print-dashboard-btn">
              <Printer className="w-4 h-4" /> Imprimir
            </Button>
            <Button
              onClick={() => setShowExport(true)}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
              data-testid="export-comerciais-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Excel {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </Button>
            <Button
              onClick={() => navigate('/dashboard/parceiros')}
              variant="outline"
              className="gap-2"
              data-testid="go-to-partners-btn"
            >
              <Handshake className="w-4 h-4" /> Ver Parceiros
            </Button>
          </div>
        </div>

        {/* Filters: Partner (cascading) + Dates */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-end gap-4">
              {isFullAccess() && (
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Parceiro</Label>
                  <Select value={partnerFilter || 'all'} onValueChange={handlePartnerChange}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="filter-partner">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os parceiros</SelectItem>
                      <SelectItem value="__internal__">Equipa Interna</SelectItem>
                      {partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Inicio</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-10 rounded-lg" data-testid="filter-start-date" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Fim</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-10 rounded-lg" data-testid="filter-end-date" />
              </div>
              <Button onClick={handleFilter} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 h-10" data-testid="filter-apply-btn">
                <Filter className="w-4 h-4" /> Filtrar
              </Button>
              {(startDate || endDate || partnerFilter) && (
                <Button onClick={handleClearFilter} variant="outline" className="h-10" data-testid="filter-clear-btn">Limpar</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totals.total_contracts || 0}</p>
                  <p className="text-xs text-slate-500">Total Contratos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totals.by_status?.concluido || 0}</p>
                  <p className="text-xs text-slate-500">Concluidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totals.by_status?.pendente || 0}</p>
                  <p className="text-xs text-slate-500">Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totals.comerciais_count || 0}</p>
                  <p className="text-xs text-slate-500">Comerciais</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comerciais Ranking Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5 text-[#2D3E6F]" />
              Ranking de Comerciais
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedIds.length === comerciais.length && comerciais.length > 0}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-comerciais"
                    />
                  </TableHead>
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">Comercial</TableHead>
                  <TableHead className="font-semibold">Funcao</TableHead>
                  <TableHead className="font-semibold">Parceiro</TableHead>
                  <TableHead className="font-semibold text-center">Total</TableHead>
                  <TableHead className="font-semibold text-center">Concluidos</TableHead>
                  <TableHead className="font-semibold text-center">Pendentes</TableHead>
                  <TableHead className="font-semibold text-center">Anulados</TableHead>
                  <TableHead className="font-semibold">Por Servico</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comerciais.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-slate-400">
                      Sem dados de comerciais
                    </TableCell>
                  </TableRow>
                ) : (
                  comerciais.map((c, index) => (
                    <TableRow
                      key={c.user_id}
                      className={`hover:bg-slate-50/50 ${selectedIds.includes(c.user_id) ? 'bg-blue-50/50' : ''}`}
                      data-testid={`comercial-row-${index}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(c.user_id)}
                          onCheckedChange={() => toggleId(c.user_id)}
                        />
                      </TableCell>
                      <TableCell className="font-bold text-[#2D3E6F]">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center gap-2 cursor-pointer group flex-1"
                            onClick={() => navigate(`/dashboard/comerciais/${c.user_id}`)}
                          >
                            <div className="w-8 h-8 rounded-full bg-[#2D3E6F] flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {c.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <span className="font-medium group-hover:text-[#2D3E6F] group-hover:underline transition-colors">{c.user_name}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/comerciais/${c.user_id}/360`); }}
                            className="text-xs text-[#2D3E6F] hover:text-[#4DD4E8] hover:underline whitespace-nowrap shrink-0 mr-2"
                            data-testid={`comercial-360-btn-${c.user_id}`}
                          >
                            Página 360
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/contracts/all?proprietario=${c.user_id}`); }}
                            className="text-xs text-[#4DD4E8] hover:text-[#2D3E6F] hover:underline whitespace-nowrap shrink-0"
                            title="Ver contratos deste comercial"
                            data-testid={`view-contracts-${index}`}
                          >
                            Ver contratos
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {ROLE_LABELS[c.role] || c.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{c.partner_name || '-'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-lg font-bold text-slate-900">{c.total_contracts}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          {c.by_status?.concluido || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          {c.by_status?.pendente || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {c.by_status?.anulado || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(c.by_category || {}).map(([cat, count]) => {
                            const config = CATEGORY_CONFIG[cat];
                            const Icon = config?.icon || Zap;
                            return (
                              <span key={cat} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600" title={config?.label || cat}>
                                <Icon className={`w-3 h-3 ${config?.color || ''}`} />
                                {count}
                              </span>
                            );
                          })}
                          {Object.keys(c.by_category || {}).length === 0 && (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Status & Category breakdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contratos por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(totals.by_status || {}).map(([status, count]) => {
                  const pct = totals.total_contracts > 0 ? (count / totals.total_contracts * 100) : 0;
                  return (
                    <div key={status}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'}`}>
                          {STATUS_LABELS[status] || status}
                        </span>
                        <span className="font-medium">{count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-[#2D3E6F] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(totals.by_status || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contratos por Servico</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(totals.by_category || {}).map(([cat, count]) => {
                  const config = CATEGORY_CONFIG[cat];
                  const Icon = config?.icon || Zap;
                  const pct = totals.total_contracts > 0 ? (count / totals.total_contracts * 100) : 0;
                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-1.5">
                          <Icon className={`w-4 h-4 ${config?.color || ''}`} />
                          {config?.label || cat}
                        </span>
                        <span className="font-medium">{count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-[#4DD4E8] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(totals.by_category || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ExportDialog
        open={showExport}
        onClose={setShowExport}
        onExport={handleExport}
        exporting={exporting}
        title={`Exportar Comerciais ${selectedIds.length > 0 ? `(${selectedIds.length})` : '(Todos)'}`}
      />
    </Layout>
  );
};
